import React, { useMemo, useState } from "react";
import {
  ListTodo, Plus, Clock, Play, Loader2, CalendarClock, Repeat, ChevronDown, Trash2, MoreHorizontal, Search, Pencil, Copy, Pause, Activity,
} from "lucide-react";
import { toast } from "sonner";
import PixelAvatar from "@/shared/components/PixelAvatar";
import { agentMood } from "@/shared/lib/agent-mood";
import TaskRunsDialog from "./TaskRunsDialog";
import { UNFINISHED } from "@/shared/api/threads";
// Lives in shared/ because the knowledge base and the directory picker use it too.
import { inputCls } from "@/shared/components/task-atoms";
import {
  Dialog, DialogContent, DialogTitle, DialogDescription,
} from "@/shared/components/ui/dialog";
import { Popover, PopoverTrigger, PopoverContent } from "@/shared/components/ui/popover";
import { SearchInput, BrandButton, AgentPage } from "@/shared/components/ui-kit";
import type { LucideIcon } from "lucide-react";
import type {
  Agent, AgentTask, ScheduleFreq, ScheduleSpec,
} from "@/shared/types";

/* ----------------------------- schedule helpers --------------------------- */

const WEEKDAYS = [
  { n: 0, short: "S", label: "Sun" },
  { n: 1, short: "M", label: "Mon" },
  { n: 2, short: "T", label: "Tue" },
  { n: 3, short: "W", label: "Wed" },
  { n: 4, short: "T", label: "Thu" },
  { n: 5, short: "F", label: "Fri" },
  { n: 6, short: "S", label: "Sat" },
];

export function fmtTime(hhmm?: string) {
  const [h, m] = (hhmm || "09:00").split(":").map(Number);
  const ampm = h >= 12 ? "pm" : "am";
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return m === 0 ? `${h12}${ampm}` : `${h12}:${String(m).padStart(2, "0")}${ampm}`;
}

function ordinal(n: number) {
  const s = ["th", "st", "nd", "rd"];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
}

// Build a cron expression from the friendly builder state.
export function buildCron({ freq, time, days, dayOfMonth }: ScheduleSpec): string | null {
  const [h, m] = (time || "09:00").split(":").map(Number);
  if (freq === "daily") return `${m} ${h} * * *`;
  if (freq === "weekly") {
    const d = days.length ? [...days].sort((a, b) => a - b).join(",") : "1";
    return `${m} ${h} * * ${d}`;
  }
  if (freq === "monthly") return `${m} ${h} ${dayOfMonth} * *`;
  return null; // custom — user types it
}

// Plain-English description of the builder state.
function describeSchedule(s: ScheduleSpec) {
  if (s.mode === "once") {
    if (!s.date) return "Runs once — pick a date";
    const d = new Date(`${s.date}T00:00:00`);
    const label = d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
    return `Runs once on ${label} at ${fmtTime(s.time)}`;
  }
  if (s.freq === "daily") return `Runs every day at ${fmtTime(s.time)}`;
  if (s.freq === "weekly") {
    const names = s.days.length
      ? [...s.days].sort((a, b) => a - b).map((n) => WEEKDAYS[n].label).join(", ")
      : "Mon";
    return `Runs every ${names} at ${fmtTime(s.time)}`;
  }
  if (s.freq === "monthly") return `Runs on the ${ordinal(s.dayOfMonth)} of every month at ${fmtTime(s.time)}`;
  return s.cron ? "Runs on your custom cron schedule" : "Type a cron expression below";
}

// Short chip label matching the sidebar ScheduleChip style ("Daily 9am", "Mon 8am").
export function chipLabel(s: ScheduleSpec): string {
  if (s.mode === "once") {
    if (!s.date) return `Once ${fmtTime(s.time)}`;
    const d = new Date(`${s.date}T00:00:00`);
    return `${d.toLocaleDateString(undefined, { month: "short", day: "numeric" })} ${fmtTime(s.time)}`;
  }
  if (s.freq === "daily") return `Daily ${fmtTime(s.time)}`;
  if (s.freq === "weekly") {
    const names = s.days.length
      ? [...s.days].sort((a, b) => a - b).map((n) => WEEKDAYS[n].label).join("/")
      : "Mon";
    return `${names} ${fmtTime(s.time)}`;
  }
  if (s.freq === "monthly") return `${ordinal(s.dayOfMonth)} monthly ${fmtTime(s.time)}`;
  return s.cron || "Custom";
}

export const initialSchedule: ScheduleSpec = {
  mode: "recurring", // "recurring" | "once"
  freq: "daily", // "daily" | "weekly" | "monthly" | "custom"
  time: "09:00",
  days: [1],
  dayOfMonth: 1,
  date: "",
  cron: "",
};

export function scheduleValid(s: ScheduleSpec): boolean {
  return s.mode === "once" ? !!s.date : s.freq !== "custom" || !!s.cron.trim();
}

/* ------------------------------ form primitives --------------------------- */

function SegButton({ active, onClick, icon: Icon, children }: {
  active: boolean;
  onClick: () => void;
  icon: LucideIcon;
  children?: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`press flex-1 flex items-center justify-center gap-1.5 h-8 rounded-[7px] text-[13px] font-medium tracking-[-0.005em] ${
        active
          ? "bg-[var(--surface)] text-[var(--text-primary)] ring-1 ring-[var(--hairline)] shadow-lift"
          : "text-[var(--text-tertiary)] hover:text-[var(--text-primary)]"
      }`}
    >
      <Icon className="w-3.5 h-3.5" strokeWidth={1.75} />
      {children}
    </button>
  );
}

export function FieldLabel({ children }: { children?: React.ReactNode }) {
  return (
    <label className="block text-[12.5px] font-medium text-[var(--text-secondary)] tracking-[-0.005em] mb-1.5">
      {children}
    </label>
  );
}

/* ------------------------------- time picker ------------------------------ */

const HOURS_12 = Array.from({ length: 12 }, (_, i) => (i === 0 ? 12 : i)); // 12, 1 … 11
const MINUTES = Array.from({ length: 12 }, (_, i) => i * 5); // 00, 05 … 55

function TimeColumn<T extends string | number>({ items, selected, onPick, render = (v) => v }: {
  items: readonly T[];
  selected: T;
  onPick: (v: T) => void;
  render?: (v: T) => React.ReactNode;
}) {
  const selRef = React.useRef<HTMLButtonElement>(null);
  React.useEffect(() => {
    selRef.current?.scrollIntoView({ block: "center" });
  }, []);
  return (
    <div className="flex-1 max-h-[176px] overflow-y-auto py-1 px-1">
      {items.map((v) => {
        const on = v === selected;
        return (
          <button
            key={v}
            ref={on ? selRef : null}
            type="button"
            onClick={() => onPick(v)}
            className={`press w-full py-[5px] rounded-[6px] text-center text-[13px] tabular-nums font-medium ${
              on
                ? "bg-[var(--brand)] text-[var(--brand-foreground)]"
                : "text-[var(--text-secondary)] hover:bg-[var(--accent-hover)] hover:text-[var(--text-primary)]"
            }`}
          >
            {render(v)}
          </button>
        );
      })}
    </div>
  );
}

// Design-system replacement for the native <input type="time"> dropdown.
function TimePicker({ value, onChange }: { value?: string; onChange: (t: string) => void }) {
  const [open, setOpen] = useState(false);
  const [h24, m] = (value || "09:00").split(":").map(Number);
  const meridiem = h24 >= 12 ? "PM" : "AM";
  const h12 = h24 % 12 === 0 ? 12 : h24 % 12;

  const commit = ({ hour = h12, minute = m, mer = meridiem }: {
    hour?: number; minute?: number; mer?: string;
  }) => {
    let h = hour % 12;
    if (mer === "PM") h += 12;
    onChange(`${String(h).padStart(2, "0")}:${String(minute).padStart(2, "0")}`);
  };

  const pad = (n: number) => String(n).padStart(2, "0");

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="press w-full h-8 px-2.5 rounded-[8px] bg-[var(--surface-sunk)] ring-1 ring-[var(--hairline)] hover:ring-[var(--hairline-strong)] flex items-center gap-2 text-[13px] text-[var(--text-primary)]"
        >
          <span className="flex-1 text-left tabular-nums">
            {pad(h12)}:{pad(m)} {meridiem}
          </span>
          <Clock className="w-3.5 h-3.5 shrink-0 text-[var(--text-tertiary)]" strokeWidth={1.75} />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={6}
        className="w-[200px] p-0 rounded-[10px] bg-[var(--canvas)] border-[var(--hairline)] shadow-menu overflow-hidden"
      >
        <div className="flex divide-x divide-[var(--hairline)]">
          <TimeColumn
            items={HOURS_12}
            selected={h12}
            onPick={(hour) => commit({ hour })}
            render={pad}
          />
          <TimeColumn
            items={MINUTES}
            selected={m}
            onPick={(minute) => commit({ minute })}
            render={pad}
          />
          <TimeColumn
            items={["AM", "PM"]}
            selected={meridiem}
            onPick={(mer) => { commit({ mer }); setOpen(false); }}
          />
        </div>
      </PopoverContent>
    </Popover>
  );
}

/* ----------------------------- schedule editor ---------------------------- */

// Reusable "Recurring / One-time" schedule builder — used by tasks and schedules.
export function ScheduleEditor({ schedule, onChange, label = "Schedule" }: {
  schedule: ScheduleSpec;
  onChange: (s: ScheduleSpec) => void;
  label?: string;
}) {
  const set = (patch: Partial<ScheduleSpec>) => onChange({ ...schedule, ...patch });
  const cron = useMemo(
    () => (schedule.freq === "custom" ? schedule.cron : buildCron(schedule)),
    [schedule]
  );

  const toggleDay = (n: number) =>
    set({
      days: schedule.days.includes(n)
        ? schedule.days.filter((d) => d !== n)
        : [...schedule.days, n],
    });

  return (
    <div>
      <FieldLabel>{label}</FieldLabel>
      <div className="flex gap-1 p-1 rounded-[9px] bg-[var(--surface-sunk)] ring-1 ring-[var(--hairline)]">
        <SegButton
          active={schedule.mode === "recurring"}
          onClick={() => set({ mode: "recurring" })}
          icon={Repeat}
        >
          Recurring
        </SegButton>
        <SegButton
          active={schedule.mode === "once"}
          onClick={() => set({ mode: "once" })}
          icon={CalendarClock}
        >
          One-time
        </SegButton>
      </div>

      {schedule.mode === "recurring" ? (
        <div className="mt-3 space-y-3">
          <div className="flex gap-2.5">
            <div className="flex-1">
              <FieldLabel>Repeats</FieldLabel>
              <div className="relative">
                <select
                  value={schedule.freq}
                  onChange={(e) => set({ freq: e.target.value as ScheduleFreq })}
                  className={`${inputCls} appearance-none pr-7 cursor-pointer`}
                >
                  <option value="daily">Every day</option>
                  <option value="weekly">Every week</option>
                  <option value="monthly">Every month</option>
                  <option value="custom">Custom (cron)</option>
                </select>
                <ChevronDown className="w-3.5 h-3.5 absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none text-[var(--text-tertiary)]" strokeWidth={1.75} />
              </div>
            </div>
            {schedule.freq !== "custom" && (
              <div className="w-[130px]">
                <FieldLabel>At</FieldLabel>
                <TimePicker value={schedule.time} onChange={(t) => set({ time: t })} />
              </div>
            )}
          </div>

          {schedule.freq === "weekly" && (
            <div>
              <FieldLabel>On days</FieldLabel>
              <div className="flex gap-1.5">
                {WEEKDAYS.map((d) => {
                  const on = schedule.days.includes(d.n);
                  return (
                    <button
                      key={d.n}
                      type="button"
                      title={d.label}
                      onClick={() => toggleDay(d.n)}
                      className={`press w-8 h-8 rounded-[8px] text-[12.5px] font-medium ${
                        on
                          ? "bg-[var(--brand)] text-[var(--brand-foreground)]"
                          : "bg-[var(--surface-sunk)] ring-1 ring-[var(--hairline)] text-[var(--text-tertiary)] hover:text-[var(--text-primary)]"
                      }`}
                    >
                      {d.short}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {schedule.freq === "monthly" && (
            <div className="w-[120px]">
              <FieldLabel>On day</FieldLabel>
              <input
                type="number"
                min={1}
                max={31}
                value={schedule.dayOfMonth}
                onChange={(e) =>
                  set({ dayOfMonth: Math.max(1, Math.min(31, Number(e.target.value) || 1)) })
                }
                className={inputCls}
              />
            </div>
          )}

          {schedule.freq === "custom" && (
            <div>
              <FieldLabel>Cron expression</FieldLabel>
              <input
                value={schedule.cron}
                onChange={(e) => set({ cron: e.target.value })}
                placeholder="0 9 * * 1-5"
                className={`${inputCls} font-mono`}
                spellCheck={false}
              />
              <div className="mt-2 grid grid-cols-5 gap-1 text-center">
                {["minute", "hour", "day", "month", "weekday"].map((f, i) => (
                  <div key={f} className="rounded-[6px] bg-[var(--surface-sunk)] ring-1 ring-[var(--hairline)] py-1">
                    <div className="font-mono text-[11px] text-[var(--text-secondary)]">
                      {(schedule.cron.trim().split(/\s+/)[i]) || "·"}
                    </div>
                    <div className="text-[9.5px] text-[var(--text-quaternary)]">{f}</div>
                  </div>
                ))}
              </div>
              <p className="text-[11.5px] leading-[1.5] text-[var(--text-quaternary)] mt-1.5 m-0">
                Five fields, left to right. <span className="font-mono">*</span> means “every”.
                Example: <span className="font-mono text-[var(--text-tertiary)]">0 9 * * 1-5</span> = 9:00am, Monday–Friday.
              </p>
            </div>
          )}
        </div>
      ) : (
        <div className="mt-3 flex gap-2.5">
          <div className="flex-1">
            <FieldLabel>Date</FieldLabel>
            <input
              type="date"
              value={schedule.date}
              onChange={(e) => set({ date: e.target.value })}
              className={inputCls}
            />
          </div>
          <div className="w-[130px]">
            <FieldLabel>At</FieldLabel>
            <TimePicker value={schedule.time} onChange={(t) => set({ time: t })} />
          </div>
        </div>
      )}

      {/* Live plain-English + cron preview */}
      <div className="mt-3 flex items-start gap-2.5 rounded-[10px] bg-[var(--surface-sunk)] ring-1 ring-[var(--hairline)] px-3 py-2.5">
        <div className="min-w-0">
          <p className="text-[12.5px] leading-[1.5] text-[var(--text-secondary)] m-0">
            {describeSchedule(schedule)}
          </p>
          {schedule.mode === "recurring" && cron && (
            <p className="font-mono text-[11px] text-[var(--text-quaternary)] mt-0.5 m-0">
              cron: {cron}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

/* ------------------------------- task dialog ------------------------------- */

/* What the pages hand the dialog. `key` rides along on the same object because
   callers use it as the remount key while passing the rest as `draft`. */
export interface TaskDraft {
  key?: string | number;
  agentId?: string;
  task?: AgentTask | null;
  spec?: ScheduleSpec | null;
}

export interface TaskDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  agents: Agent[];
  draft: TaskDraft;
  /** May return the write's promise: the dialog closes when it resolves and
      stays open, with what was typed, when it rejects. */
  onSave: (agentId: string, task: AgentTask, editing: boolean) => void | Promise<void>;
  onDelete?: (agentId: string, taskId: string) => void;
}

// Shared create / edit / delete dialog used by Tasks and Schedule.
// `draft` = { agentId, task|null, spec|null }. onSave(agentId, task, editing).
// Callers remount (via key / conditional render) to reset the form state.
export function TaskDialog({ open, onOpenChange, agents, draft, onSave, onDelete }: TaskDialogProps) {
  const editing = !!draft?.task;
  const [agentId, setAgentId] = useState(draft?.agentId || agents[0]?.id);
  const [name, setName] = useState(draft?.task?.name || "");
  const [prompt, setPrompt] = useState(draft?.task?.prompt || "");
  const [schedule, setSchedule] = useState(draft?.task?.spec || draft?.spec || initialSchedule);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [saving, setSaving] = useState(false);

  const agent = agents.find((a) => a.id === agentId) || agents[0];
  const canSave = name.trim() && prompt.trim() && scheduleValid(schedule);

  const save = async () => {
    setSaving(true);
    try {
      await saveTask();
      onOpenChange(false);
    } catch {
      // the refusal was toasted where it happened; the form stays for a fix
    } finally {
      setSaving(false);
    }
  };

  const saveTask = () =>
    onSave(agentId, {
      ...(draft?.task || { id: `task-${Date.now()}` }),
      name: name.trim(),
      prompt: prompt.trim(),
      schedule: chipLabel(schedule),
      cron: schedule.mode === "recurring" ? (schedule.freq === "custom" ? schedule.cron : buildCron(schedule)) : null,
      spec: schedule,
    }, editing);

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) setConfirmDelete(false); onOpenChange(v); }}>
      <DialogContent className="max-w-[480px] p-0 gap-0 rounded-[16px] bg-[var(--canvas)] border-[var(--hairline-strong)] shadow-dialog overflow-hidden">
        {/* Header */}
        <div className="px-5 pt-5 pb-4 border-b hairline">
          <DialogTitle className="text-[15px] font-semibold tracking-[-0.01em] text-[var(--text-primary)] flex items-center gap-2">
            <ListTodo className="w-4 h-4 text-[var(--text-tertiary)]" strokeWidth={1.75} />
            {editing ? "Edit task" : "New task"}
          </DialogTitle>
          <DialogDescription className="text-[12.5px] text-[var(--text-tertiary)] mt-1 flex items-center gap-1.5">
            {agent && <PixelAvatar seed={agent.seed} hue={agent.hue} emotion={agentMood(agent)} size={13} rounded={4} />}
            {agent?.name} runs this on the schedule you set.
          </DialogDescription>
        </div>

        <div className="px-5 py-4 space-y-4 max-h-[65vh] overflow-y-auto">
          {/* Agent — selector only when there's a real choice to make */}
          {agents.length > 1 && (
            <div>
              <FieldLabel>Agent</FieldLabel>
              {editing ? (
                <div className="flex items-center gap-2 h-8 px-2.5 rounded-[8px] bg-[var(--surface-sunk)] ring-1 ring-[var(--hairline)] text-[13px] text-[var(--text-primary)]">
                  <PixelAvatar seed={agent.seed} hue={agent.hue} emotion={agentMood(agent)} size={15} rounded={4} />
                  {agent.name}
                </div>
              ) : (
                <div className="relative">
                  <select
                    value={agentId}
                    onChange={(e) => setAgentId(e.target.value)}
                    className={`${inputCls} appearance-none pr-7 cursor-pointer`}
                  >
                    {agents.map((a) => (
                      <option key={a.id} value={a.id}>{a.name}</option>
                    ))}
                  </select>
                  <ChevronDown className="w-3.5 h-3.5 absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none text-[var(--text-tertiary)]" strokeWidth={1.75} />
                </div>
              )}
            </div>
          )}

          {/* Name */}
          <div>
            <FieldLabel>Task name</FieldLabel>
            <input
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Morning news digest"
              className={inputCls}
            />
          </div>

          {/* Instruction */}
          <div>
            <FieldLabel>Instruction</FieldLabel>
            <textarea
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder={`What should ${agent?.name || "the agent"} do each time this runs?`}
              rows={4}
              className="w-full px-2.5 py-2 rounded-[8px] bg-[var(--surface-sunk)] ring-1 ring-[var(--hairline)] focus:ring-[var(--hairline-focus)] outline-none text-[13px] leading-[1.55] text-[var(--text-primary)] placeholder:text-[var(--text-quaternary)] resize-none"
            />
          </div>

          <ScheduleEditor schedule={schedule} onChange={setSchedule} />
        </div>

        {/* Footer */}
        <div className="px-5 py-3.5 border-t hairline flex items-center gap-2">
          {confirmDelete ? (
            <>
              <span className="text-[13px] text-[var(--text-secondary)] truncate">
                Delete “{name.trim() || "this task"}”?
              </span>
              <div className="flex-1" />
              <button
                onClick={() => setConfirmDelete(false)}
                className="press h-8 px-3.5 rounded-[8px] text-[13px] font-medium text-[var(--text-secondary)] hover:bg-[var(--accent-hover)] hover:text-[var(--text-primary)]"
              >
                Keep task
              </button>
              <button
                /* Only reachable from the Delete button, which renders when editing. */
                onClick={() => { onDelete?.(agentId, draft.task!.id); onOpenChange(false); }}
                className="press h-8 px-4 rounded-[8px] text-[13px] font-medium bg-[var(--danger)] text-white hover:bg-[var(--danger-hover)] flex items-center gap-1.5"
              >
                <Trash2 className="w-3.5 h-3.5" strokeWidth={1.75} />
                Delete
              </button>
            </>
          ) : (
            <>
              {editing && onDelete && (
                <button
                  onClick={() => setConfirmDelete(true)}
                  className="press h-8 px-3 rounded-[8px] text-[13px] font-medium text-[var(--danger)] hover:bg-[var(--danger-bg)] flex items-center gap-1.5"
                >
                  <Trash2 className="w-3.5 h-3.5" strokeWidth={1.75} />
                  Delete
                </button>
              )}
              <div className="flex-1" />
              <button
                onClick={() => onOpenChange(false)}
                className="press h-8 px-3.5 rounded-[8px] text-[13px] font-medium text-[var(--text-secondary)] hover:bg-[var(--accent-hover)] hover:text-[var(--text-primary)]"
              >
                Cancel
              </button>
              <button
                onClick={save}
                disabled={!canSave || saving}
                className="press h-8 px-4 rounded-[8px] text-[13px] font-medium bg-[var(--brand)] text-[var(--brand-foreground)] hover:bg-[var(--brand-hover)] disabled:opacity-40 disabled:pointer-events-none"
              >
                {saving ? "Saving…" : editing ? "Save changes" : "Save task"}
              </button>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* -------------------------------- task list ------------------------------- */

/** Where a row is in the run cycle. Absent = idle. */
export type RunState = "running";

export function RunButton({ state, onRun, disabled }: {
  state?: RunState;
  onRun: () => void;
  disabled: boolean;
}) {
  if (state === "running") {
    return (
      <span className="shrink-0 flex items-center gap-1.5 h-7 px-3 rounded-[7px] text-[12.5px] font-medium bg-[var(--success-bg)] text-[var(--success)] ring-1 ring-[var(--success-ring)]">
        <Loader2 className="w-3 h-3 animate-spin" strokeWidth={2.25} />
        Running
      </span>
    );
  }
  return (
    <button
      onClick={onRun}
      disabled={disabled}
      className="press shrink-0 flex items-center gap-1.5 h-7 px-3 rounded-[7px] text-[12.5px] font-medium bg-[var(--surface)] ring-1 ring-[var(--hairline-strong)] text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:ring-[var(--hairline-focus)] disabled:opacity-40 disabled:pointer-events-none"
    >
      <Play className="w-3 h-3" strokeWidth={2} />
      Run
    </button>
  );
}

export function ScheduleBadge({ children }: { children?: React.ReactNode }) {
  return (
    <span className="shrink-0 whitespace-nowrap flex items-center gap-[4px] font-mono text-[10.5px] font-medium tracking-[0.04em] px-2 py-[3px] rounded-full text-[var(--text-tertiary)] bg-[var(--surface-2)]">
      <Clock className="w-[9px] h-[9px]" strokeWidth={2} />
      {children}
    </span>
  );
}

/* ------------------------------ next-run math ------------------------------ */

const pad2 = (n: number) => String(n).padStart(2, "0");

function fmtWhen(d: Date) {
  const midnight = (x: Date) => { const y = new Date(x); y.setHours(0, 0, 0, 0); return y; };
  // Subtracting Dates is JS coercion; the casts say so without changing the code.
  const diff = Math.round(
    ((midnight(d) as unknown as number) - (midnight(new Date()) as unknown as number)) / 86400000
  );
  const t = fmtTime(`${pad2(d.getHours())}:${pad2(d.getMinutes())}`);
  if (diff === 0) return `today ${t}`;
  if (diff === 1) return `tomorrow ${t}`;
  if (diff === -1) return `yesterday ${t}`;
  return `${d.toLocaleDateString(undefined, { month: "short", day: "numeric" })} ${t}`;
}

// a task's next slot is the server's: it knows the cron and the zone
function nextRunLabel(task: AgentTask): string {
  if (task.paused) return "Paused";
  return task.nextRunAt ? `Next run ${fmtWhen(new Date(task.nextRunAt))}` : "No upcoming runs";
}

/** How a task's last run ended, when that is worth a word. */
const ENDED_BADLY: Partial<Record<string, string>> = { error: "failed", halted: "stopped" };

function lastRunLabel(task: AgentTask) {
  if (!task.lastRun) return "Never ran";
  const d = new Date(task.lastRun);
  // isNaN coerces its argument; the cast says so without changing the call.
  return isNaN(d as unknown as number) ? `Last ran ${task.lastRun}` : `Last ran ${fmtWhen(d)}`;
}

/* -------------------------------- row menu --------------------------------- */

// Shared row actions menu — the item just needs `.paused`.
export function RowMenu({ task, onEdit, onDuplicate, onDelete, onTogglePause, onRuns }: {
  task: { paused?: boolean };
  onEdit: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
  onTogglePause: () => void;
  /** The runs the task started. */
  onRuns: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const close = () => { setOpen(false); setConfirming(false); };
  const itemCls =
    "press w-full flex items-center gap-2 px-2 h-7 rounded-[7px] text-[13px] font-medium text-[var(--text-secondary)] hover:bg-[var(--accent-hover)] hover:text-[var(--text-primary)]";
  const PauseIcon = task.paused ? Play : Pause;
  return (
    <Popover open={open} onOpenChange={(v) => { setOpen(v); if (!v) setConfirming(false); }}>
      <PopoverTrigger asChild>
        <button
          onClick={(e) => e.stopPropagation()}
          title="Task actions"
          className={`press w-7 h-7 rounded-[7px] flex items-center justify-center text-[var(--text-tertiary)] hover:text-[var(--text-primary)] hover:bg-[var(--accent-hover)] ${
            open ? "" : "opacity-0 group-hover:opacity-100 focus-visible:opacity-100"
          }`}
        >
          <MoreHorizontal className="w-4 h-4" strokeWidth={1.75} />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        sideOffset={4}
        onClick={(e) => e.stopPropagation()}
        className="w-[172px] p-1 rounded-[10px] bg-[var(--canvas)] border-[var(--hairline)] shadow-menu"
      >
        <button className={itemCls} onClick={() => { close(); onEdit(); }}>
          <Pencil className="w-3.5 h-3.5" strokeWidth={1.75} /> Edit
        </button>
        <button className={itemCls} onClick={() => { close(); onDuplicate(); }}>
          <Copy className="w-3.5 h-3.5" strokeWidth={1.75} /> Duplicate
        </button>
        <button className={itemCls} onClick={() => { close(); onRuns(); }}>
          <Activity className="w-3.5 h-3.5" strokeWidth={1.75} /> Runs
        </button>
        <button className={itemCls} onClick={() => { close(); onTogglePause(); }}>
          <PauseIcon className="w-3.5 h-3.5" strokeWidth={1.75} /> {task.paused ? "Resume" : "Pause"}
        </button>
        <div className="h-px bg-[var(--hairline)] my-1" />
        {confirming ? (
          <button
            className="press w-full flex items-center gap-2 px-2 h-7 rounded-[7px] text-[13px] font-medium bg-[var(--danger)] text-white hover:bg-[var(--danger-hover)]"
            onClick={() => { close(); onDelete(); }}
          >
            <Trash2 className="w-3.5 h-3.5" strokeWidth={1.75} /> Confirm delete
          </button>
        ) : (
          <button
            className="press w-full flex items-center gap-2 px-2 h-7 rounded-[7px] text-[13px] font-medium text-[var(--danger)] hover:bg-[var(--danger-bg)]"
            onClick={() => setConfirming(true)}
          >
            <Trash2 className="w-3.5 h-3.5" strokeWidth={1.75} /> Delete
          </button>
        )}
      </PopoverContent>
    </Popover>
  );
}

/* -------------------------------- task row --------------------------------- */

function TaskRow({ task, runState, onRun, onEdit, onDuplicate, onDelete, onTogglePause, onRuns, onOpenChat }: {
  task: AgentTask;
  runState?: RunState;
  onRun: () => void;
  onEdit: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
  onTogglePause: () => void;
  onRuns: () => void;
  onOpenChat?: (threadId: string) => void;
}) {
  const next = nextRunLabel(task);
  const endedBadly = task.lastRunStatus ? ENDED_BADLY[task.lastRunStatus] : undefined;
  const threadId = task.lastRunThreadId;
  return (
    <div
      onClick={onEdit}
      className="group flex items-center gap-3 px-4 py-3 hover:bg-[var(--accent-hover)] cursor-pointer"
    >
      <div className={`min-w-0 flex-1 ${task.paused ? "opacity-55" : ""}`}>
        <div className="flex items-start gap-2 min-w-0">
          <span className="min-w-0 break-words text-[14px] font-medium text-[var(--text-primary)] tracking-[-0.005em]">
            {task.name}
          </span>
          {task.paused && (
            <span className="shrink-0 text-[10.5px] font-medium px-1.5 py-[2px] rounded-full text-[var(--warn-text)] bg-[var(--warn-bg)] ring-1 ring-[var(--warn-ring)]">
              Paused
            </span>
          )}
        </div>
        <div className="text-[12.5px] leading-[1.5] text-[var(--text-tertiary)] truncate mt-[1px]">
          {task.prompt}
        </div>
        {/* Next run and last run each stay whole; on a narrow row the last run moves to a second line */}
        <div className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5 mt-[3px] text-[11.5px] text-[var(--text-quaternary)]">
          <span className="inline-flex items-center gap-1.5 min-w-0 max-w-full">
            <CalendarClock className="w-3 h-3 shrink-0" strokeWidth={1.75} />
            <span className="whitespace-nowrap">{next}</span>
          </span>
          <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
            <span aria-hidden>·</span>
            {threadId && onOpenChat ? (
              <button
                onClick={(e) => { e.stopPropagation(); onOpenChat(threadId); }}
                title={endedBadly ? task.lastRunError || undefined : "Open the chat it answered in"}
                className="press hover:text-[var(--text-primary)] hover:underline underline-offset-2"
              >
                {lastRunLabel(task)}
              </button>
            ) : (
              <span>{lastRunLabel(task)}</span>
            )}
          </span>
          {endedBadly && (
            <span className="shrink-0 rounded-full text-[10.5px] font-medium px-1.5 py-[1px] bg-[var(--danger-bg)] text-[var(--danger)] ring-1 ring-[var(--danger-ring)]">
              {endedBadly}
            </span>
          )}
        </div>
      </div>
      {/* Schedule chip yields on phones — the row's meta line already says it */}
      <span className="hidden sm:inline-flex shrink-0"><ScheduleBadge>{task.schedule}</ScheduleBadge></span>
      <div className="shrink-0 flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
        <RunButton state={runState} onRun={onRun} disabled={!!task.paused} />
        <RowMenu
          task={task}
          onEdit={onEdit}
          onDuplicate={onDuplicate}
          onDelete={onDelete}
          onTogglePause={onTogglePause}
          onRuns={onRuns}
        />
      </div>
    </div>
  );
}

/* ---------------------------------- page ---------------------------------- */

export interface TasksPageProps {
  agent: Agent;
  /** App passes the whole roster; the page only ever renders `agent`.
      Declared because it is passed; see the smell register. */
  agents?: Agent[];
  /* The writes resolve when the API answered, and reject when it refused. */
  onAddTask?: (task: AgentTask) => void | Promise<void>;
  onUpdateTask?: (taskId: string, patch: Partial<AgentTask>) => void | Promise<void>;
  onDeleteTask?: (taskId: string) => void | Promise<void>;
  onRunTask?: (task: AgentTask) => void;
  /** Opens a chat a server task's run answered in. */
  onOpenChat?: (threadId: string) => void;
}

/** `done` once the write landed. A refusal was already toasted where it
    happened, so it is swallowed here rather than left unhandled. */
export function whenSaved(write: void | Promise<void> | undefined, done: () => void) {
  Promise.resolve(write).then(done, () => {});
}

export default function TasksPage({ agent, onAddTask, onUpdateTask, onDeleteTask, onRunTask, onOpenChat }: TasksPageProps) {
  const [draft, setDraft] = useState<TaskDraft | null>(null); // TaskDialog payload
  const [runsId, setRunsId] = useState<string | null>(null); // task id shown in TaskRunsDialog
  const [query, setQuery] = useState("");
  const runState = (task: AgentTask): RunState | undefined =>
    task.lastRunStatus && UNFINISHED.includes(task.lastRunStatus) ? "running" : undefined;

  const tasks = agent.tasks || [];
  const q = query.trim().toLowerCase();
  const visible = q
    ? tasks.filter((t) => `${t.name} ${t.prompt}`.toLowerCase().includes(q))
    : tasks;

  const openCreate = () => setDraft({ key: `new-${Date.now()}`, agentId: agent.id, task: null, spec: null });
  const openEdit = (task: AgentTask) => setDraft({ key: `edit-${task.id}`, agentId: agent.id, task });

  const saveDraft = async (_agentId: string, task: AgentTask, editing: boolean) => {
    if (editing) {
      const { id, ...fields } = task;
      await onUpdateTask?.(id, fields);
      toast(`“${task.name}” updated`, { description: task.schedule });
    } else {
      await onAddTask?.(task);
      toast(`“${task.name}” scheduled`, { description: task.schedule });
    }
  };

  const duplicateTask = (task: AgentTask) => {
    const { lastRun, versions, ...rest } = task; // copy starts its own history
    whenSaved(onAddTask?.({ ...rest, id: `task-${Date.now()}`, name: `${task.name} (copy)`, paused: false }), () =>
      toast(`“${task.name}” duplicated`),
    );
  };

  const deleteTask = (task: AgentTask) => {
    whenSaved(onDeleteTask?.(task.id), () => toast(`“${task.name}” deleted`));
  };

  const togglePause = (task: AgentTask) => {
    whenSaved(onUpdateTask?.(task.id, { paused: !task.paused }), () =>
      toast(task.paused ? `“${task.name}” resumed` : `“${task.name}” paused`, {
        description: task.paused ? "Back on its schedule." : "Scheduled runs are suspended.",
      }),
    );
  };

  return (
    <main className="flex-1 min-w-0 flex flex-col bg-[var(--canvas)]">
      <AgentPage
        agent={agent}
        title={`${agent.name} tasks`}
        subtitle={
          tasks.length === 0
            ? "No tasks yet"
            : `${tasks.length} scheduled task${tasks.length === 1 ? "" : "s"}`
        }
        description={
          <>
            A task is an instruction {agent.name} runs on a schedule — every day, every week,
            or once at a set time. Add one and it runs on its own; you can also trigger it
            manually with <span className="font-medium text-[var(--text-primary)]">Run</span>.
          </>
        }
        actions={
          <>
            {tasks.length > 5 && (
              <SearchInput
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search tasks"
                className="w-[170px] shrink-0"
              />
            )}
            <BrandButton onClick={openCreate} className="shrink-0">
              <Plus className="w-3.5 h-3.5" strokeWidth={2} />
              New task
            </BrandButton>
          </>
        }
      >

          {tasks.length > 0 && visible.length > 0 ? (
            <div className="tray-box overflow-hidden divide-y divide-[var(--hairline)]">
              {visible.map((t) => (
                <TaskRow
                  key={t.id}
                  task={t}
                  runState={runState(t)}
                  // a run is real: it starts at once and the row follows its status
                  onRun={() => onRunTask?.(t)}
                  onEdit={() => openEdit(t)}
                  onDuplicate={() => duplicateTask(t)}
                  onDelete={() => deleteTask(t)}
                  onTogglePause={() => togglePause(t)}
                  onRuns={() => setRunsId(t.id)}
                  onOpenChat={onOpenChat}
                />
              ))}
            </div>
          ) : tasks.length > 0 ? (
            <div className="py-14 text-center tray-box">
              <Search className="w-5 h-5 mx-auto mb-2 text-[var(--text-quaternary)]" strokeWidth={1.5} />
              <div className="text-[14px] font-medium text-[var(--text-primary)]">No matching tasks</div>
              <div className="text-[12.5px] text-[var(--text-tertiary)] mt-1">
                Nothing matches “{query.trim()}”. Try a different search.
              </div>
            </div>
          ) : (
            <div className="py-14 text-center tray-box">
              <ListTodo className="w-5 h-5 mx-auto mb-2 text-[var(--text-quaternary)]" strokeWidth={1.5} />
              <div className="text-[14px] font-medium text-[var(--text-primary)]">No tasks yet</div>
              <div className="text-[12.5px] text-[var(--text-tertiary)] mt-1 mb-4">
                Schedule {agent.name} to work for you — daily digests, weekly sweeps, one-time jobs.
              </div>
              <BrandButton onClick={openCreate}>
                <Plus className="w-3.5 h-3.5" strokeWidth={2} />
                New task
              </BrandButton>
            </div>
          )}
      </AgentPage>

      {runsId && (
        <TaskRunsDialog
          open
          onOpenChange={(v) => !v && setRunsId(null)}
          task={tasks.find((t) => t.id === runsId)}
          agent={agent}
          onOpenChat={(threadId) => { setRunsId(null); onOpenChat?.(threadId); }}
        />
      )}

      {draft && (
        <TaskDialog
          key={draft.key}
          open
          onOpenChange={(v) => !v && setDraft(null)}
          agents={[agent]}
          draft={draft}
          onSave={saveDraft}
          onDelete={(_agentId, taskId) => {
            const t = tasks.find((x) => x.id === taskId);
            if (t) deleteTask(t);
          }}
        />
      )}
    </main>
  );
}
